// src/pages/SignalMonitor/SignalMonitorPage.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Tooltip,
  LinearProgress,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Info as InfoIcon,
  Send as SendIcon,  // ✅ Correct: Send as SendIcon
} from '@mui/icons-material';
import API_URLS, { WS_CONFIG } from '../../config/api.js';

// ============================================================================
// CONSTANTS
// ============================================================================

const STATUS_COLORS = {
  COMPLETED: '#4CAF50',
  WAITING: '#2196F3',
  IDLE: '#9E9E9E',
  IN_POSITION: '#FF9800',
};

const SIGNAL_TYPE_COLORS = {
  entry: '#4CAF50',
  exit: '#F44336',
  early: '#2196F3',
  confirmation: '#FFC107',
  bulk_entry: '#4CAF50',
  bulk_exit: '#F44336',
  early_confirmation_refresh: '#FF9800',
};

// ============================================================================
// SIGNAL BROADCASTER DIALOG
// ============================================================================

function SignalBroadcasterDialog({ open, onClose, onBroadcast, loading }) {
  const [signal, setSignal] = useState('');
  const [price, setPrice] = useState('');
  const [signalType, setSignalType] = useState('entry');
  const [error, setError] = useState('');

  const handleBroadcast = () => {
    if (!signal.trim()) {
      setError('Signal name is required');
      return;
    }

    if (signalType === 'entry' && !price) {
      setError('Price is required for entry signals');
      return;
    }

    onBroadcast({
      signal: signal.trim(),
      price: price ? parseFloat(price) : null,
      type: signalType,
    });

    setSignal('');
    setPrice('');
    setSignalType('entry');
    setError('');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Broadcast Signal</DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <TextField
          fullWidth
          label="Signal Name"
          value={signal}
          onChange={(e) => {
            setSignal(e.target.value);
            setError('');
          }}
          placeholder="e.g., COBRA_LONG"
          sx={{ mb: 2 }}
        />

        <TextField
          select
          fullWidth
          label="Signal Type"
          value={signalType}
          onChange={(e) => setSignalType(e.target.value)}
          SelectProps={{
            native: true,
          }}
          sx={{ mb: 2 }}
        >
          <option value="entry">Entry</option>
          <option value="exit">Exit</option>
        </TextField>

        {signalType === 'entry' && (
          <TextField
            fullWidth
            label="Price"
            type="number"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            inputProps={{ step: '0.01', min: '0' }}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleBroadcast}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
        >
          {loading ? 'Broadcasting...' : 'Broadcast'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ============================================================================
// SIGNAL HISTORY DIALOG
// ============================================================================

function SignalHistoryDialog({ open, onClose, bulkStrategyId, loading }) {
  const [signals, setSignals] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !bulkStrategyId) return;

    const fetchHistory = async () => {
      try {
        setError('');
        const response = await fetch(
          API_URLS.BULK_STRATEGY_SIGNAL_HISTORY(bulkStrategyId)
        );

        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const data = await response.json();
        setSignals(data.signals || []);
      } catch (err) {
        setError(`Failed to fetch signal history: ${err.message}`);
      }
    };

    fetchHistory();
  }, [open, bulkStrategyId]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Signal History - {bulkStrategyId}</DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell>Timestamp</TableCell>
                  <TableCell>Signal Type</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell>Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {signals.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                      No signals recorded yet
                    </TableCell>
                  </TableRow>
                ) : (
                  signals.map((signal) => (
                    <TableRow key={signal.id} hover>
                      <TableCell sx={{ fontSize: '0.85rem' }}>
                        {signal.timestamp_formatted || 'N/A'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={signal.signal_type}
                          size="small"
                          sx={{
                            backgroundColor:
                              SIGNAL_TYPE_COLORS[signal.signal_type] ||
                              '#9E9E9E',
                            color: '#fff',
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.85rem' }}>
                        {signal.source === 'bulk_strategy'
                          ? 'Bulk'
                          : 'Sub-Strategy'}
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.85rem' }}>
                        {signal.data?.message ||
                          signal.data?.entry_signal ||
                          'N/A'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ============================================================================
// STATUS BADGE COMPONENT
// ============================================================================

function StatusBadge({ status }) {
  return (
    <Chip
      label={status}
      size="small"
      sx={{
        backgroundColor: STATUS_COLORS[status] || '#9E9E9E',
        color: '#fff',
        fontWeight: 500,
      }}
    />
  );
}

// ============================================================================
// PROGRESS INDICATOR
// ============================================================================

function ProgressIndicator({ completed, total }) {
  const percentage = total > 0 ? (completed / total) * 100 : 0;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box sx={{ flex: 1 }}>
        <LinearProgress
          variant="determinate"
          value={percentage}
          sx={{
            height: 6,
            borderRadius: 1,
          }}
        />
      </Box>
      <Typography variant="caption" sx={{ minWidth: 40 }}>
        {completed}/{total}
      </Typography>
    </Box>
  );
}

// ============================================================================
// BULK STRATEGY TABLE ROW
// ============================================================================

function BulkStrategyRow({
  strategy,
  onRefresh,
  onHistory,
  onExport,
  onDelete,
  onBroadcast,
}) {
  const [anchorEl, setAnchorEl] = useState(null);

  const inPosition = strategy.current_state?.in_position || false;
  const completedCount = Object.keys(
    strategy.current_state?.completed_strategies || {}
  ).length;
  const totalCount = strategy.progress?.total || 0;

  return (
    <TableRow
      hover
      sx={{
        backgroundColor: inPosition ? '#FFF3E0' : 'inherit',
        borderLeft: inPosition ? '4px solid #FF9800' : 'none',
      }}
    >
      {/* Strategy ID */}
      <TableCell sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
        {strategy.bulk_strategy_id}
      </TableCell>

      {/* Symbol & Timeframe */}
      <TableCell sx={{ fontSize: '0.85rem' }}>
        {strategy.symbol} {strategy.timeframe}
      </TableCell>

      {/* Status */}
      <TableCell>
        <StatusBadge
          status={inPosition ? 'IN_POSITION' : 'IDLE'}
        />
      </TableCell>

      {/* Progress */}
      <TableCell sx={{ minWidth: 150 }}>
        <ProgressIndicator
          completed={completedCount}
          total={totalCount}
        />
      </TableCell>

      {/* Entry Price */}
      <TableCell sx={{ fontSize: '0.85rem', textAlign: 'right' }}>
        {inPosition && strategy.current_state?.entry_price
          ? `$${parseFloat(strategy.current_state.entry_price).toFixed(2)}`
          : '—'}
      </TableCell>

      {/* Time in Position */}
      <TableCell sx={{ fontSize: '0.85rem' }}>
        {inPosition && strategy.current_state?.entry_time
          ? formatHoldTime(strategy.current_state.entry_time)
          : '—'}
      </TableCell>

      {/* Actions */}
      <TableCell align="right">
        <Tooltip title="Refresh Status">
          <IconButton
            size="small"
            onClick={() => onRefresh(strategy.bulk_strategy_id)}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="View History">
          <IconButton
            size="small"
            onClick={() => onHistory(strategy.bulk_strategy_id)}
          >
            <InfoIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Export Timeline">
          <IconButton
            size="small"
            onClick={() => onExport(strategy.bulk_strategy_id)}
          >
            <DownloadIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Delete Strategy">
          <IconButton
            size="small"
            onClick={() => onDelete(strategy.bulk_strategy_id)}
            sx={{ color: '#F44336' }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

function formatHoldTime(entryTimeStr) {
  try {
    const entryTime = new Date(entryTimeStr);
    const now = new Date();
    const minutes = Math.floor((now - entryTime) / (1000 * 60));

    if (minutes < 1) return '<1m';
    if (minutes < 60) return `${minutes}m`;

    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  } catch {
    return 'N/A';
  }
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function SignalMonitorPage() {
  const [strategies, setStrategies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [broadcasting, setBroadcasting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Dialog states
  const [broadcasterOpen, setBroadcasterOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [selectedStrategy, setSelectedStrategy] = useState(null);

  // WebSocket reference
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  // =========================================================================
  // FETCH STRATEGIES
  // =========================================================================

  const fetchStrategies = useCallback(async () => {
    try {
      setError('');
      setLoading(true);

      const response = await fetch(API_URLS.BULK_STRATEGY_LIST);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to fetch strategies`);
      }

      const data = await response.json();

      if (data.success && Array.isArray(data.bulk_strategies)) {
        setStrategies(data.bulk_strategies);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      setError(`Failed to load strategies: ${err.message}`);
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // =========================================================================
  // WEBSOCKET CONNECTION
  // =========================================================================

  const connectWebSocket = useCallback(() => {
    try {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        return;
      }

      const wsUrl = WS_CONFIG.URL;
      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => {
        console.log('✅ WebSocket connected');
        setError('');
      };

      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data.type === 'trading_alert') {
            const alertData = data.message || data;

            // Update strategy if it matches
            setStrategies((prev) =>
              prev.map((strategy) => {
                if (
                  strategy.bulk_strategy_id ===
                  (alertData.bulk_strategy_id ||
                    alertData.strategy_id)
                ) {
                  return {
                    ...strategy,
                    current_state: {
                      ...strategy.current_state,
                      in_position: alertData.in_position ?? strategy.current_state?.in_position,
                      entry_price: alertData.entry_price ?? strategy.current_state?.entry_price,
                      entry_time: alertData.entry_time ?? strategy.current_state?.entry_time,
                    },
                  };
                }
                return strategy;
              })
            );

            // Show success message
            setSuccessMessage(alertData.message || 'Alert received');
            setTimeout(() => setSuccessMessage(''), 5000);
          }
        } catch (err) {
          console.error('WebSocket message parse error:', err);
        }
      };

      wsRef.current.onerror = (error) => {
        console.error('WebSocket error:', error);
        setError('WebSocket connection error - attempting to reconnect');
      };

      wsRef.current.onclose = () => {
        console.warn('WebSocket disconnected - will reconnect');

        // Attempt reconnection
        reconnectTimeoutRef.current = setTimeout(() => {
          connectWebSocket();
        }, WS_CONFIG.RECONNECT_INTERVAL);
      };
    } catch (err) {
      console.error('WebSocket connection failed:', err);
      setError(`WebSocket error: ${err.message}`);
    }
  }, []);

  // =========================================================================
  // SIGNAL BROADCAST
  // =========================================================================

  const handleBroadcastSignal = async (signalData) => {
    try {
      setBroadcasting(true);
      setError('');

      const endpoint =
        signalData.type === 'exit'
          ? API_URLS.SIGNAL_EXIT_BROADCAST
          : API_URLS.SIGNAL_BROADCAST;

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          signal: signalData.signal,
          price: signalData.price,
          timestamp: new Date().toISOString(),
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const result = await response.json();

      setSuccessMessage(
        `✅ Signal "${signalData.signal}" broadcast successfully`
      );
      setTimeout(() => setSuccessMessage(''), 5000);

      setBroadcasterOpen(false);

      // Refresh strategies after brief delay
      setTimeout(() => fetchStrategies(), 500);
    } catch (err) {
      setError(`Failed to broadcast signal: ${err.message}`);
      console.error('Broadcast error:', err);
    } finally {
      setBroadcasting(false);
    }
  };

  // =========================================================================
  // EXPORT TO EXCEL
  // =========================================================================

  const handleExportToExcel = async (bulkStrategyId) => {
    try {
      setError('');

      const response = await fetch(
        API_URLS.BULK_STRATEGY_EXPORT_EXCEL(bulkStrategyId)
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${bulkStrategyId}_timeline.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setSuccessMessage(`✅ Timeline exported for ${bulkStrategyId}`);
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      setError(`Failed to export timeline: ${err.message}`);
      console.error('Export error:', err);
    }
  };

  // =========================================================================
  // DELETE STRATEGY
  // =========================================================================

  const handleDeleteStrategy = async (bulkStrategyId) => {
    if (!window.confirm(`Delete strategy ${bulkStrategyId}?`)) {
      return;
    }

    try {
      setError('');

      const response = await fetch(
        API_URLS.BULK_STRATEGY_DELETE(bulkStrategyId),
        {
          method: 'DELETE',
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      setSuccessMessage(`✅ Strategy ${bulkStrategyId} deleted`);
      setTimeout(() => setSuccessMessage(''), 5000);

      // Remove from list
      setStrategies((prev) =>
        prev.filter((s) => s.bulk_strategy_id !== bulkStrategyId)
      );
    } catch (err) {
      setError(`Failed to delete strategy: ${err.message}`);
      console.error('Delete error:', err);
    }
  };

  // =========================================================================
  // REFRESH STRATEGY
  // =========================================================================

  const handleRefreshStrategy = async (bulkStrategyId) => {
    try {
      setError('');

      const response = await fetch(
        API_URLS.BULK_STRATEGY_STATUS(bulkStrategyId)
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        setStrategies((prev) =>
          prev.map((s) =>
            s.bulk_strategy_id === bulkStrategyId
              ? { ...s, ...data }
              : s
          )
        );

        setSuccessMessage(`✅ Strategy ${bulkStrategyId} refreshed`);
        setTimeout(() => setSuccessMessage(''), 3000);
      }
    } catch (err) {
      setError(`Failed to refresh strategy: ${err.message}`);
      console.error('Refresh error:', err);
    }
  };

  // =========================================================================
  // LIFECYCLE
  // =========================================================================

  useEffect(() => {
    fetchStrategies();
    connectWebSocket();

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [fetchStrategies, connectWebSocket]);

  // =========================================================================
  // RENDER
  // =========================================================================

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Signal Monitor
        </Typography>
        <Button
          variant="contained"
          onClick={() => setBroadcasterOpen(true)}
          startIcon={<SendIcon />}
        >
          Broadcast Signal
        </Button>
      </Box>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}

      {/* Main Content */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : strategies.length === 0 ? (
        <Card>
          <CardContent sx={{ py: 6, textAlign: 'center' }}>
            <Typography color="textSecondary">
              No bulk strategies configured yet
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader
            title={`Active Strategies (${strategies.length})`}
            action={
              <Button
                size="small"
                onClick={fetchStrategies}
                startIcon={<RefreshIcon />}
              >
                Refresh
              </Button>
            }
          />
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Strategy ID</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Symbol</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Progress</TableCell>
                  <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>
                    Entry Price
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Hold Time</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {strategies.map((strategy) => (
                  <BulkStrategyRow
                    key={strategy.bulk_strategy_id}
                    strategy={strategy}
                    onRefresh={handleRefreshStrategy}
                    onHistory={() => {
                      setSelectedStrategy(strategy.bulk_strategy_id);
                      setHistoryOpen(true);
                    }}
                    onExport={handleExportToExcel}
                    onDelete={handleDeleteStrategy}
                    onBroadcast={() => setBroadcasterOpen(true)}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {/* Dialogs */}
      <SignalBroadcasterDialog
        open={broadcasterOpen}
        onClose={() => setBroadcasterOpen(false)}
        onBroadcast={handleBroadcastSignal}
        loading={broadcasting}
      />

      <SignalHistoryDialog
        open={historyOpen}
        onClose={() => {
          setHistoryOpen(false);
          setSelectedStrategy(null);
        }}
        bulkStrategyId={selectedStrategy}
        loading={loading}
      />
    </Box>
  );
}


// // src/SignalMonitor/SignalMonitorPage.js
// // Standalone Signal Monitor Dashboard Page
// // Complete monitoring solution with internal strategy selection

// import React, { useState, useEffect, useRef, useCallback } from 'react';
// import {
//   Box,
//   Grid,
//   Paper,
//   Typography,
//   Table,
//   TableBody,
//   TableCell,
//   TableHead,
//   TableRow,
//   Chip,
//   LinearProgress,
//   Card,
//   CardContent,
//   IconButton,
//   Tooltip,
//   Alert,
//   Tabs,
//   Tab,
//   Button,
//   CircularProgress,
//   Divider,
//   Select,
//   MenuItem,
//   FormControl,
//   InputLabel,
//   TextField,
//   ToggleButton,
//   ToggleButtonGroup,
//   Badge,
//   Fade,
//   Grow,
//   AppBar,
//   Toolbar,
//   Container,
//   Backdrop,
//   Snackbar
// } from '@mui/material';
// import {
//   TrendingUp,
//   TrendingDown,
//   AccessTime,
//   CheckCircle,
//   Warning,
//   Error,
//   Refresh,
//   PlayArrow,
//   Stop,
//   Assessment,
//   Timeline,
//   AttachMoney,
//   Speed,
//   Visibility,
//   Dashboard,
//   FilterList,
//   Download,
//   Settings,
//   Notifications,
//   NotificationsActive,
//   NotificationsOff,
//   FullscreenExit,
//   Fullscreen,
//   Search,
//   ClearAll,
//   ShowChart,
//   BarChart
// } from '@mui/icons-material';

// const SignalMonitorPage = () => {
//   // ============= STATE MANAGEMENT =============
//   // Strategy Selection
//   const [availableStrategies, setAvailableStrategies] = useState([]);
//   const [selectedBulkStrategy, setSelectedBulkStrategy] = useState('ALL');
//   const [isLoadingStrategies, setIsLoadingStrategies] = useState(true);
  
//   // Dashboard Data
//   const [dashboardData, setDashboardData] = useState(null);
//   const [realtimeSignals, setRealtimeSignals] = useState([]);
//   const [isLoading, setIsLoading] = useState(true);
//   const [error, setError] = useState(null);
  
//   // Monitoring Controls
//   const [isMonitoring, setIsMonitoring] = useState(true);
//   const [selectedTimeRange, setSelectedTimeRange] = useState(24); // hours
//   const [refreshInterval, setRefreshInterval] = useState(1000); // milliseconds
//   const [activeTab, setActiveTab] = useState(0);
  
//   // UI Controls
//   const [isFullscreen, setIsFullscreen] = useState(false);
//   const [soundEnabled, setSoundEnabled] = useState(true);
//   const [showNotifications, setShowNotifications] = useState(true);
//   const [filterSignalType, setFilterSignalType] = useState('ALL');
//   const [searchTerm, setSearchTerm] = useState('');
//   const [notification, setNotification] = useState(null);
  
//   // WebSocket and Polling
//   const ws = useRef(null);
//   const pollingInterval = useRef(null);
//   const audioRef = useRef(new Audio('/sounds/notification.mp3')); // Add your audio file

//   // ============= INITIAL LOAD - FETCH STRATEGIES =============
//   useEffect(() => {
//     const loadAvailableStrategies = async () => {
//       setIsLoadingStrategies(true);
//       try {
//         const response = await fetch('/strategies/api/bulk-strategy/list/');
//         const data = await response.json();
        
//         if (data.success) {
//           setAvailableStrategies(data.bulk_strategies || []);
          
//           // Auto-select first strategy if available
//           if (data.bulk_strategies && data.bulk_strategies.length > 0) {
//             // Optionally auto-select the first active strategy
//             const activeStrategy = data.bulk_strategies.find(s => s.is_active);
//             if (activeStrategy) {
//               setSelectedBulkStrategy(activeStrategy.bulk_strategy_id);
//             }
//           }
//         }
//       } catch (err) {
//         console.error('Error loading strategies:', err);
//         setError('Failed to load strategies');
//       } finally {
//         setIsLoadingStrategies(false);
//       }
//     };

//     loadAvailableStrategies();
//   }, []);

//   // ============= FETCH DASHBOARD DATA =============
//   const fetchDashboardData = useCallback(async () => {
//     try {
//       const endpoint = selectedBulkStrategy === 'ALL' 
//         ? `/strategies/api/signal-audit/dashboard/?hours=${selectedTimeRange}`
//         : `/strategies/api/signal-audit/dashboard/${selectedBulkStrategy}/?hours=${selectedTimeRange}`;
        
//       const response = await fetch(endpoint);
//       const data = await response.json();
      
//       if (data.success) {
//         setDashboardData(data);
//         setError(null);
        
//         // Check for alerts
//         if (data.active_trades && data.active_trades.length > 0) {
//           data.active_trades.forEach(trade => {
//             if (trade.hold_minutes > 60) {
//               showNotification(`Long hold alert: ${trade.bulk_strategy_id} - ${trade.hold_minutes}m`, 'warning');
//             }
//           });
//         }
//       }
//     } catch (err) {
//       console.error('Error fetching dashboard data:', err);
//       setError('Failed to fetch dashboard data');
//     }
//   }, [selectedBulkStrategy, selectedTimeRange]);

//   // ============= FETCH REAL-TIME SIGNALS =============
//   const fetchRealtimeSignals = useCallback(async () => {
//     try {
//       const response = await fetch(`/strategies/api/signal-monitor/realtime/?seconds=60`);
//       const data = await response.json();
      
//       if (data.success) {
//         setRealtimeSignals(prevSignals => {
//           const newSignals = data.signals.filter(
//             newSig => !prevSignals.some(oldSig => 
//               oldSig.timestamp === newSig.timestamp && 
//               oldSig.strategy_id === newSig.strategy_id
//             )
//           );
          
//           // Play sound for new entry signals
//           if (soundEnabled && newSignals.length > 0) {
//             newSignals.forEach(signal => {
//               if (signal.signal_type === 'ENTRY') {
//                 playSound('entry');
//                 showNotification(`ENTRY: ${signal.strategy_id}`, 'success');
//               } else if (signal.signal_type === 'EXIT') {
//                 playSound('exit');
//                 showNotification(`EXIT: ${signal.strategy_id}`, 'info');
//               }
//             });
//           }
          
//           return data.signals;
//         });
//       }
//     } catch (err) {
//       console.error('Error fetching realtime signals:', err);
//     }
//   }, [soundEnabled]);

//   // ============= WEBSOCKET CONNECTION =============
//   useEffect(() => {
//     if (isMonitoring) {
//       // Connect to WebSocket
//       const wsUrl = `wss://ojuimo.com/ws/signal-audit/`;
//       ws.current = new WebSocket(wsUrl);
      
//       ws.current.onopen = () => {
//         console.log('WebSocket connected for signal monitoring');
//         showNotification('Connected to live signal feed', 'success');
//       };
      
//       ws.current.onmessage = (event) => {
//         const data = JSON.parse(event.data);
//         if (data.type === 'signal_update') {
//           // Prepend new signal
//           setRealtimeSignals(prev => [data.message, ...prev].slice(0, 100));
          
//           // Refresh dashboard if it's our strategy
//           if (selectedBulkStrategy === 'ALL' || 
//               data.message.bulk_strategy_id === selectedBulkStrategy) {
//             fetchDashboardData();
//           }
          
//           // Alert on important signals
//           if (data.message.signal_type === 'ENTRY' && soundEnabled) {
//             playSound('entry');
//             showNotification(`New Entry: ${data.message.strategy_id}`, 'success');
//           }
//         }
//       };
      
//       ws.current.onerror = (error) => {
//         console.error('WebSocket error:', error);
//         showNotification('WebSocket connection error', 'error');
//       };
      
//       ws.current.onclose = () => {
//         showNotification('WebSocket disconnected', 'warning');
//       };
      
//       // Start polling
//       pollingInterval.current = setInterval(() => {
//         fetchDashboardData();
//         fetchRealtimeSignals();
//       }, refreshInterval);
      
//       return () => {
//         if (ws.current) {
//           ws.current.close();
//         }
//         if (pollingInterval.current) {
//           clearInterval(pollingInterval.current);
//         }
//       };
//     }
//   }, [isMonitoring, selectedBulkStrategy, refreshInterval, fetchDashboardData, fetchRealtimeSignals]);

//   // ============= INITIAL DATA LOAD =============
//   useEffect(() => {
//     if (selectedBulkStrategy) {
//       setIsLoading(true);
//       Promise.all([fetchDashboardData(), fetchRealtimeSignals()])
//         .finally(() => setIsLoading(false));
//     }
//   }, [selectedBulkStrategy, fetchDashboardData, fetchRealtimeSignals]);

//   // ============= UTILITY FUNCTIONS =============
//   const formatTimestamp = (timestamp) => {
//     if (!timestamp) return '--';
//     const date = new Date(timestamp);
//     return date.toLocaleString('en-US', {
//       hour: '2-digit',
//       minute: '2-digit',
//       second: '2-digit',
//       hour12: false
//     });
//   };

//   const getTimeAgo = (seconds) => {
//     if (seconds < 60) return `${Math.floor(seconds)}s`;
//     if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
//     if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
//     return `${Math.floor(seconds / 86400)}d`;
//   };

//   const getStatusColor = (status) => {
//     switch (status) {
//       case 'COMPLETED': return 'success';
//       case 'WAITING': return 'warning';
//       case 'IDLE': return 'default';
//       case 'ACTIVE': return 'info';
//       case 'WIN': return 'success';
//       case 'LOSS': return 'error';
//       case 'ENTRY': return 'success';
//       case 'EXIT': return 'error';
//       default: return 'default';
//     }
//   };

//   const playSound = (type) => {
//     if (!soundEnabled) return;
//     try {
//       // You can have different sounds for different events
//       if (type === 'entry') {
//         audioRef.current.play();
//       } else if (type === 'exit') {
//         // Use a different sound or same
//         audioRef.current.play();
//       }
//     } catch (err) {
//       console.error('Error playing sound:', err);
//     }
//   };

//   const showNotification = (message, severity = 'info') => {
//     if (!showNotifications) return;
//     setNotification({ message, severity });
//   };

//   const exportData = () => {
//     // Export current view data to CSV
//     const dataToExport = activeTab === 0 ? realtimeSignals : dashboardData?.signal_timeline || [];
//     const csv = dataToExport.map(item => {
//       return `${item.timestamp},${item.signal_type},${item.strategy_id || item.bulk_strategy_id},${item.price || ''}`;
//     }).join('\n');
    
//     const blob = new Blob([csv], { type: 'text/csv' });
//     const url = window.URL.createObjectURL(blob);
//     const a = document.createElement('a');
//     a.href = url;
//     a.download = `signals_export_${Date.now()}.csv`;
//     a.click();
    
//     showNotification('Data exported successfully', 'success');
//   };

//   const clearAllSignals = () => {
//     setRealtimeSignals([]);
//     showNotification('Signal history cleared', 'info');
//   };

//   const toggleFullscreen = () => {
//     if (!document.fullscreenElement) {
//       document.documentElement.requestFullscreen();
//       setIsFullscreen(true);
//     } else {
//       document.exitFullscreen();
//       setIsFullscreen(false);
//     }
//   };

//   // ============= FILTERED SIGNALS =============
//   const filteredSignals = realtimeSignals.filter(signal => {
//     if (filterSignalType !== 'ALL' && signal.signal_type !== filterSignalType) {
//       return false;
//     }
//     if (searchTerm && !JSON.stringify(signal).toLowerCase().includes(searchTerm.toLowerCase())) {
//       return false;
//     }
//     return true;
//   });

//   // ============= RENDER LOADING =============
//   if (isLoadingStrategies) {
//     return (
//       <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', bgcolor: '#0a0e1a' }}>
//         <CircularProgress size={60} />
//       </Box>
//     );
//   }

//   // ============= MAIN RENDER =============
//   return (
//     <Box sx={{ bgcolor: '#0a0e1a', minHeight: '100vh' }}>
//       {/* App Bar */}
//       <AppBar position="sticky" sx={{ bgcolor: '#1a1f2e' }}>
//         <Toolbar>
//           <Dashboard sx={{ mr: 2 }} />
//           <Typography variant="h6" sx={{ flexGrow: 1 }}>
//             Signal Monitor Dashboard
//           </Typography>
          
//           {/* Strategy Selector */}
//           <FormControl sx={{ minWidth: 200, mr: 2 }}>
//             <Select
//               value={selectedBulkStrategy}
//               onChange={(e) => setSelectedBulkStrategy(e.target.value)}
//               size="small"
//               sx={{ 
//                 color: '#fff',
//                 '& .MuiOutlinedInput-notchedOutline': { borderColor: '#4a5568' }
//               }}
//             >
//               <MenuItem value="ALL">All Strategies</MenuItem>
//               {availableStrategies.map(strategy => (
//                 <MenuItem key={strategy.bulk_strategy_id} value={strategy.bulk_strategy_id}>
//                   {strategy.bulk_strategy_id} {strategy.current_state?.in_position && '📍'}
//                 </MenuItem>
//               ))}
//             </Select>
//           </FormControl>

//           {/* Time Range Selector */}
//           <FormControl sx={{ minWidth: 100, mr: 2 }}>
//             <Select
//               value={selectedTimeRange}
//               onChange={(e) => setSelectedTimeRange(e.target.value)}
//               size="small"
//               sx={{ color: '#fff' }}
//             >
//               <MenuItem value={1}>1H</MenuItem>
//               <MenuItem value={6}>6H</MenuItem>
//               <MenuItem value={24}>24H</MenuItem>
//               <MenuItem value={72}>3D</MenuItem>
//               <MenuItem value={168}>7D</MenuItem>
//             </Select>
//           </FormControl>

//           {/* Control Buttons */}
//           <Tooltip title="Export Data">
//             <IconButton onClick={exportData} sx={{ color: '#fff' }}>
//               <Download />
//             </IconButton>
//           </Tooltip>
          
//           <Tooltip title={soundEnabled ? "Mute" : "Unmute"}>
//             <IconButton onClick={() => setSoundEnabled(!soundEnabled)} sx={{ color: '#fff' }}>
//               {soundEnabled ? <NotificationsActive /> : <NotificationsOff />}
//             </IconButton>
//           </Tooltip>

//           <Tooltip title="Toggle Fullscreen">
//             <IconButton onClick={toggleFullscreen} sx={{ color: '#fff' }}>
//               {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
//             </IconButton>
//           </Tooltip>

//           <Tooltip title={isMonitoring ? "Stop Monitoring" : "Start Monitoring"}>
//             <IconButton 
//               onClick={() => setIsMonitoring(!isMonitoring)} 
//               sx={{ color: isMonitoring ? '#4caf50' : '#f44336' }}
//             >
//               {isMonitoring ? <PlayArrow /> : <Stop />}
//             </IconButton>
//           </Tooltip>

//           <Tooltip title="Refresh">
//             <IconButton onClick={() => { fetchDashboardData(); fetchRealtimeSignals(); }} sx={{ color: '#fff' }}>
//               <Refresh />
//             </IconButton>
//           </Tooltip>
//         </Toolbar>
//       </AppBar>

//       <Container maxWidth={false} sx={{ pt: 3 }}>
//         {/* Summary Cards */}
//         <Grid container spacing={2} sx={{ mb: 3 }}>
//           {/* Active Trades Card */}
//           <Grid item xs={12} sm={6} md={3}>
//             <Card sx={{ bgcolor: '#1a1f2e', border: '1px solid #2d3748' }}>
//               <CardContent>
//                 <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
//                   <Badge 
//                     badgeContent={dashboardData?.active_trades?.length || 0} 
//                     color="success"
//                   >
//                     <TrendingUp sx={{ color: '#4caf50', mr: 1 }} />
//                   </Badge>
//                   <Typography variant="subtitle2" sx={{ color: '#94a3b8', ml: 1 }}>
//                     Active Trades
//                   </Typography>
//                 </Box>
//                 <Typography variant="h4" sx={{ color: '#fff' }}>
//                   {dashboardData?.active_trades?.length || 0}
//                 </Typography>
//                 {dashboardData?.active_trades?.length > 0 && (
//                   <Typography variant="caption" sx={{ color: '#64748b' }}>
//                     Avg hold: {Math.round(
//                       dashboardData.active_trades.reduce((sum, t) => sum + t.hold_minutes, 0) / 
//                       dashboardData.active_trades.length
//                     )}m
//                   </Typography>
//                 )}
//               </CardContent>
//             </Card>
//           </Grid>

//           {/* P&L Card */}
//           <Grid item xs={12} sm={6} md={3}>
//             <Card sx={{ bgcolor: '#1a1f2e', border: '1px solid #2d3748' }}>
//               <CardContent>
//                 <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
//                   <AttachMoney sx={{ color: '#ffc107', mr: 1 }} />
//                   <Typography variant="subtitle2" sx={{ color: '#94a3b8' }}>
//                     Total P&L ({selectedTimeRange}h)
//                   </Typography>
//                 </Box>
//                 <Typography 
//                   variant="h4" 
//                   sx={{ 
//                     color: (dashboardData?.pnl_summary?.total_pnl || 0) >= 0 ? '#4caf50' : '#f44336' 
//                   }}
//                 >
//                   ${dashboardData?.pnl_summary?.total_pnl || 0}
//                 </Typography>
//                 <Box sx={{ display: 'flex', gap: 1 }}>
//                   <Chip
//                     label={`Win: ${dashboardData?.pnl_summary?.win_rate || 0}%`}
//                     size="small"
//                     sx={{ bgcolor: 'rgba(76, 175, 80, 0.2)', color: '#4caf50' }}
//                   />
//                   <Chip
//                     label={`${dashboardData?.pnl_summary?.wins || 0}W/${dashboardData?.pnl_summary?.losses || 0}L`}
//                     size="small"
//                     variant="outlined"
//                     sx={{ color: '#94a3b8' }}
//                   />
//                 </Box>
//               </CardContent>
//             </Card>
//           </Grid>

//           {/* Signals Card */}
//           <Grid item xs={12} sm={6} md={3}>
//             <Card sx={{ bgcolor: '#1a1f2e', border: '1px solid #2d3748' }}>
//               <CardContent>
//                 <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
//                   <Speed sx={{ color: '#2196f3', mr: 1 }} />
//                   <Typography variant="subtitle2" sx={{ color: '#94a3b8' }}>
//                     Signals ({selectedTimeRange}h)
//                   </Typography>
//                 </Box>
//                 <Typography variant="h4" sx={{ color: '#fff' }}>
//                   {dashboardData?.signal_statistics?.TOTAL || 0}
//                 </Typography>
//                 <Typography variant="caption" sx={{ color: '#64748b' }}>
//                   Entry: {dashboardData?.signal_statistics?.ENTRY || 0} | 
//                   Exit: {dashboardData?.signal_statistics?.EXIT || 0} | 
//                   Refresh: {dashboardData?.signal_statistics?.REFRESHES || 0}
//                 </Typography>
//               </CardContent>
//             </Card>
//           </Grid>

//           {/* Condition Status Card */}
//           <Grid item xs={12} sm={6} md={3}>
//             <Card sx={{ bgcolor: '#1a1f2e', border: '1px solid #2d3748' }}>
//               <CardContent>
//                 <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
//                   <Assessment sx={{ color: '#9c27b0', mr: 1 }} />
//                   <Typography variant="subtitle2" sx={{ color: '#94a3b8' }}>
//                     Conditions Status
//                   </Typography>
//                 </Box>
//                 <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 2 }}>
//                   {Object.values(dashboardData?.condition_progression || {}).slice(0, 6).map((cond) => (
//                     <Chip
//                       key={cond.strategy_id}
//                       label={cond.current_status[0]}
//                       size="small"
//                       color={getStatusColor(cond.current_status)}
//                       sx={{ fontSize: '0.7rem' }}
//                     />
//                   ))}
//                 </Box>
//               </CardContent>
//             </Card>
//           </Grid>
//         </Grid>

//         {/* Main Content Area */}
//         <Paper sx={{ bgcolor: '#1a1f2e', mb: 2 }}>
//           <Tabs
//             value={activeTab}
//             onChange={(e, newValue) => setActiveTab(newValue)}
//             sx={{ 
//               borderBottom: 1, 
//               borderColor: 'divider',
//               '& .MuiTab-root': { color: '#94a3b8' },
//               '& .Mui-selected': { color: '#fff' }
//             }}
//           >
//             <Tab label={`Live Signals (${filteredSignals.length})`} />
//             <Tab label="Signal Timeline" />
//             <Tab label="Condition Tracking" />
//             <Tab label="Trade History" />
//             <Tab label="Performance Analytics" />
//           </Tabs>
//         </Paper>

//         {/* Tab Content */}
//         <Fade in={true}>
//           <Box>
//             {/* Live Signals Tab */}
//             {activeTab === 0 && (
//               <Paper sx={{ bgcolor: '#1a1f2e', p: 2 }}>
//                 {/* Filters */}
//                 <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
//                   <TextField
//                     placeholder="Search signals..."
//                     value={searchTerm}
//                     onChange={(e) => setSearchTerm(e.target.value)}
//                     size="small"
//                     InputProps={{
//                       startAdornment: <Search sx={{ color: '#94a3b8', mr: 1 }} />
//                     }}
//                     sx={{ 
//                       '& input': { color: '#fff' },
//                       '& .MuiOutlinedInput-root': { 
//                         '& fieldset': { borderColor: '#4a5568' }
//                       }
//                     }}
//                   />
                  
//                   <ToggleButtonGroup
//                     value={filterSignalType}
//                     exclusive
//                     onChange={(e, newType) => setFilterSignalType(newType || 'ALL')}
//                     size="small"
//                   >
//                     <ToggleButton value="ALL">All</ToggleButton>
//                     <ToggleButton value="ENTRY">Entry</ToggleButton>
//                     <ToggleButton value="EXIT">Exit</ToggleButton>
//                     <ToggleButton value="SIGNAL">Signal</ToggleButton>
//                   </ToggleButtonGroup>
                  
//                   <Button
//                     startIcon={<ClearAll />}
//                     onClick={clearAllSignals}
//                     size="small"
//                     variant="outlined"
//                     sx={{ ml: 'auto' }}
//                   >
//                     Clear
//                   </Button>
//                 </Box>

//                 {/* Signals Table */}
//                 <Table size="small">
//                   <TableHead>
//                     <TableRow>
//                       <TableCell sx={{ color: '#94a3b8' }}>Time</TableCell>
//                       <TableCell sx={{ color: '#94a3b8' }}>Age</TableCell>
//                       <TableCell sx={{ color: '#94a3b8' }}>Strategy</TableCell>
//                       <TableCell sx={{ color: '#94a3b8' }}>Type</TableCell>
//                       <TableCell sx={{ color: '#94a3b8' }}>Signal</TableCell>
//                       <TableCell sx={{ color: '#94a3b8' }}>Price</TableCell>
//                       <TableCell sx={{ color: '#94a3b8' }}>Status</TableCell>
//                     </TableRow>
//                   </TableHead>
//                   <TableBody>
//                     {filteredSignals.map((signal, index) => (
//                       <Grow in={true} key={index}>
//                         <TableRow
//                           sx={{ 
//                             bgcolor: signal.is_recent ? 'rgba(76, 175, 80, 0.1)' : 'transparent',
//                             animation: signal.is_recent ? 'pulse 1s' : 'none',
//                             '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.05)' }
//                           }}
//                         >
//                           <TableCell sx={{ color: '#fff' }}>
//                             {formatTimestamp(signal.timestamp)}
//                           </TableCell>
//                           <TableCell>
//                             <Chip
//                               label={getTimeAgo(signal.age_seconds)}
//                               size="small"
//                               sx={{
//                                 bgcolor: signal.age_seconds < 10 ? 'rgba(76, 175, 80, 0.2)' : 'transparent',
//                                 color: signal.age_seconds < 10 ? '#4caf50' : '#64748b'
//                               }}
//                             />
//                           </TableCell>
//                           <TableCell sx={{ color: '#fff', fontSize: '0.875rem' }}>
//                             {signal.strategy_id}
//                           </TableCell>
//                           <TableCell>
//                             <Chip
//                               label={signal.signal_type}
//                               size="small"
//                               color={getStatusColor(signal.signal_type)}
//                             />
//                           </TableCell>
//                           <TableCell sx={{ color: '#94a3b8' }}>
//                             {signal.data?.signal || signal.condition_name || '--'}
//                           </TableCell>
//                           <TableCell sx={{ color: '#ffc107', fontWeight: 600 }}>
//                             {signal.data?.price ? `$${signal.data.price}` : '--'}
//                           </TableCell>
//                           <TableCell>
//                             {signal.is_recent && (
//                               <Badge variant="dot" color="success">
//                                 <Chip
//                                   label="NEW"
//                                   size="small"
//                                   color="success"
//                                   sx={{ animation: 'pulse 1s infinite' }}
//                                 />
//                               </Badge>
//                             )}
//                           </TableCell>
//                         </TableRow>
//                       </Grow>
//                     ))}
//                   </TableBody>
//                 </Table>

//                 {filteredSignals.length === 0 && (
//                   <Box sx={{ textAlign: 'center', py: 4 }}>
//                     <Typography variant="body2" sx={{ color: '#64748b' }}>
//                       No signals found matching filters
//                     </Typography>
//                   </Box>
//                 )}
//               </Paper>
//             )}

//             {/* Signal Timeline Tab */}
//             {activeTab === 1 && (
//               <Paper sx={{ bgcolor: '#1a1f2e', p: 2 }}>
//                 <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
//                   Signal Timeline
//                 </Typography>
//                 <Box sx={{ maxHeight: 600, overflow: 'auto' }}>
//                   {dashboardData?.signal_timeline?.map((event, index) => (
//                     <Fade in={true} key={index}>
//                       <Box
//                         sx={{
//                           display: 'flex',
//                           alignItems: 'center',
//                           p: 2,
//                           borderLeft: '3px solid',
//                           borderLeftColor: 
//                             event.signal_type === 'ENTRY' ? '#4caf50' :
//                             event.signal_type === 'EXIT' ? '#f44336' :
//                             event.signal_type === 'REFRESH' ? '#ffc107' :
//                             '#2196f3',
//                           mb: 2,
//                           bgcolor: 'rgba(255, 255, 255, 0.02)',
//                           borderRadius: 1,
//                           '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.05)' }
//                         }}
//                       >
//                         <Box sx={{ flex: 1 }}>
//                           <Typography variant="subtitle2" sx={{ color: '#fff' }}>
//                             {event.timestamp_lagos}
//                           </Typography>
//                           <Typography variant="body2" sx={{ color: '#94a3b8' }}>
//                             {event.signal_type} - {event.bulk_strategy_id || event.strategy_id}
//                           </Typography>
//                           {event.details && (
//                             <Typography variant="caption" sx={{ color: '#64748b' }}>
//                               {JSON.stringify(event.details).substring(0, 100)}...
//                             </Typography>
//                           )}
//                         </Box>
//                         <Box sx={{ display: 'flex', gap: 1 }}>
//                           <Chip
//                             label={event.source}
//                             size="small"
//                             variant="outlined"
//                             sx={{ color: '#94a3b8' }}
//                           />
//                           {event.details?.price && (
//                             <Chip
//                               label={`$${event.details.price}`}
//                               size="small"
//                               sx={{ bgcolor: 'rgba(255, 193, 7, 0.2)', color: '#ffc107' }}
//                             />
//                           )}
//                         </Box>
//                       </Box>
//                     </Fade>
//                   ))}
//                 </Box>
//               </Paper>
//             )}

//             {/* Condition Tracking Tab */}
//             {activeTab === 2 && (
//               <Paper sx={{ bgcolor: '#1a1f2e', p: 2 }}>
//                 <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
//                   Condition Progression
//                 </Typography>
//                 <Grid container spacing={2}>
//                   {Object.values(dashboardData?.condition_progression || {}).map((condition) => (
//                     <Grid item xs={12} sm={6} md={4} key={condition.strategy_id}>
//                       <Card sx={{ bgcolor: '#0f1419', border: '1px solid #2d3748' }}>
//                         <CardContent>
//                           <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
//                             <Typography variant="subtitle2" sx={{ color: '#fff' }}>
//                               {condition.strategy_id}
//                             </Typography>
//                             <Chip
//                               label={condition.current_status}
//                               size="small"
//                               color={getStatusColor(condition.current_status)}
//                             />
//                           </Box>
                          
//                           {condition.is_confirmation && (
//                             <Chip
//                               label="CONFIRMATION"
//                               size="small"
//                               color="secondary"
//                               sx={{ mb: 1 }}
//                             />
//                           )}
                          
//                           <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
//                             Type: {condition.condition_type}
//                           </Typography>
                          
//                           {condition.signals_received?.length > 0 && (
//                             <Box sx={{ mt: 1 }}>
//                               <Typography variant="caption" sx={{ color: '#94a3b8' }}>
//                                 Signals: {condition.signals_received.join(', ')}
//                               </Typography>
//                             </Box>
//                           )}
                          
//                           {condition.early_signal_time && (
//                             <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
//                               Early: {formatTimestamp(condition.early_signal_time)}
//                             </Typography>
//                           )}
                          
//                           {condition.completion_time && (
//                             <Typography variant="caption" sx={{ color: '#4caf50', display: 'block' }}>
//                               Complete: {formatTimestamp(condition.completion_time)}
//                             </Typography>
//                           )}
                          
//                           {/* Progress Bar */}
//                           <LinearProgress
//                             variant="determinate"
//                             value={
//                               condition.current_status === 'COMPLETED' ? 100 :
//                               condition.current_status === 'WAITING' ? 50 : 0
//                             }
//                             sx={{ mt: 2, bgcolor: '#2d3748' }}
//                             color={getStatusColor(condition.current_status)}
//                           />
//                         </CardContent>
//                       </Card>
//                     </Grid>
//                   ))}
//                 </Grid>
//               </Paper>
//             )}

//             {/* Trade History Tab */}
//             {activeTab === 3 && (
//               <Paper sx={{ bgcolor: '#1a1f2e', p: 2 }}>
//                 <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
//                   Recent Trades
//                 </Typography>
                
//                 {/* Active Trades Section */}
//                 {dashboardData?.active_trades?.length > 0 && (
//                   <Box sx={{ mb: 4 }}>
//                     <Typography variant="subtitle1" sx={{ color: '#4caf50', mb: 2 }}>
//                       Active Positions ({dashboardData.active_trades.length})
//                     </Typography>
//                     <Table size="small">
//                       <TableHead>
//                         <TableRow>
//                           <TableCell sx={{ color: '#94a3b8' }}>Strategy</TableCell>
//                           <TableCell sx={{ color: '#94a3b8' }}>Entry Time</TableCell>
//                           <TableCell sx={{ color: '#94a3b8' }}>Entry Price</TableCell>
//                           <TableCell sx={{ color: '#94a3b8' }}>Hold Time</TableCell>
//                           <TableCell sx={{ color: '#94a3b8' }}>Exit Signal</TableCell>
//                         </TableRow>
//                       </TableHead>
//                       <TableBody>
//                         {dashboardData.active_trades.map((trade, index) => (
//                           <TableRow key={index}>
//                             <TableCell sx={{ color: '#fff' }}>
//                               {trade.bulk_strategy_id}
//                             </TableCell>
//                             <TableCell sx={{ color: '#94a3b8' }}>
//                               {formatTimestamp(trade.entry_time)}
//                             </TableCell>
//                             <TableCell sx={{ color: '#ffc107' }}>
//                               ${trade.entry_price}
//                             </TableCell>
//                             <TableCell>
//                               <Chip
//                                 label={`${trade.hold_minutes}m`}
//                                 size="small"
//                                 color={trade.hold_minutes > 60 ? 'warning' : 'default'}
//                               />
//                             </TableCell>
//                             <TableCell sx={{ color: '#94a3b8' }}>
//                               {trade.exit_signal}
//                             </TableCell>
//                           </TableRow>
//                         ))}
//                       </TableBody>
//                     </Table>
//                   </Box>
//                 )}
                
//                 {/* Closed Trades Section */}
//                 <Typography variant="subtitle1" sx={{ color: '#94a3b8', mb: 2 }}>
//                   Closed Trades
//                 </Typography>
//                 <Table>
//                   <TableHead>
//                     <TableRow>
//                       <TableCell sx={{ color: '#94a3b8' }}>Entry</TableCell>
//                       <TableCell sx={{ color: '#94a3b8' }}>Exit</TableCell>
//                       <TableCell sx={{ color: '#94a3b8' }}>Hold Time</TableCell>
//                       <TableCell sx={{ color: '#94a3b8' }}>P&L</TableCell>
//                       <TableCell sx={{ color: '#94a3b8' }}>Result</TableCell>
//                     </TableRow>
//                   </TableHead>
//                   <TableBody>
//                     {dashboardData?.pnl_summary?.recent_trades?.map((trade, index) => (
//                       <TableRow key={index}>
//                         <TableCell sx={{ color: '#fff' }}>
//                           ${trade.entry_price}
//                         </TableCell>
//                         <TableCell sx={{ color: '#fff' }}>
//                           ${trade.exit_price}
//                         </TableCell>
//                         <TableCell sx={{ color: '#94a3b8' }}>
//                           {trade.hold_minutes}m
//                         </TableCell>
//                         <TableCell sx={{ 
//                           color: trade.pnl >= 0 ? '#4caf50' : '#f44336',
//                           fontWeight: 600
//                         }}>
//                           ${trade.pnl}
//                         </TableCell>
//                         <TableCell>
//                           <Chip
//                             label={trade.result}
//                             size="small"
//                             color={trade.result === 'WIN' ? 'success' : 'error'}
//                           />
//                         </TableCell>
//                       </TableRow>
//                     ))}
//                   </TableBody>
//                 </Table>
//               </Paper>
//             )}

//             {/* Performance Analytics Tab */}
//             {activeTab === 4 && (
//               <Paper sx={{ bgcolor: '#1a1f2e', p: 2 }}>
//                 <Typography variant="h6" sx={{ color: '#fff', mb: 2 }}>
//                   Performance Analytics
//                 </Typography>
                
//                 <Grid container spacing={3}>
//                   {/* Performance Metrics */}
//                   <Grid item xs={12} md={6}>
//                     <Card sx={{ bgcolor: '#0f1419' }}>
//                       <CardContent>
//                         <Typography variant="subtitle1" sx={{ color: '#fff', mb: 2 }}>
//                           Trading Metrics
//                         </Typography>
                        
//                         <Box sx={{ mb: 2 }}>
//                           <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
//                             <Typography variant="body2" sx={{ color: '#94a3b8' }}>
//                               Total Trades
//                             </Typography>
//                             <Typography variant="body2" sx={{ color: '#fff' }}>
//                               {dashboardData?.pnl_summary?.total_trades || 0}
//                             </Typography>
//                           </Box>
                          
//                           <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
//                             <Typography variant="body2" sx={{ color: '#94a3b8' }}>
//                               Win Rate
//                             </Typography>
//                             <Typography variant="body2" sx={{ color: '#4caf50' }}>
//                               {dashboardData?.pnl_summary?.win_rate || 0}%
//                             </Typography>
//                           </Box>
                          
//                           <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
//                             <Typography variant="body2" sx={{ color: '#94a3b8' }}>
//                               Avg Hold Time
//                             </Typography>
//                             <Typography variant="body2" sx={{ color: '#fff' }}>
//                               {dashboardData?.pnl_summary?.avg_hold_time_minutes || 0}m
//                             </Typography>
//                           </Box>
                          
//                           <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
//                             <Typography variant="body2" sx={{ color: '#94a3b8' }}>
//                               Total P&L
//                             </Typography>
//                             <Typography 
//                               variant="body2" 
//                               sx={{ 
//                                 color: (dashboardData?.pnl_summary?.total_pnl || 0) >= 0 ? '#4caf50' : '#f44336',
//                                 fontWeight: 600
//                               }}
//                             >
//                               ${dashboardData?.pnl_summary?.total_pnl || 0}
//                             </Typography>
//                           </Box>
//                         </Box>
//                       </CardContent>
//                     </Card>
//                   </Grid>
                  
//                   {/* Signal Statistics */}
//                   <Grid item xs={12} md={6}>
//                     <Card sx={{ bgcolor: '#0f1419' }}>
//                       <CardContent>
//                         <Typography variant="subtitle1" sx={{ color: '#fff', mb: 2 }}>
//                           Signal Statistics
//                         </Typography>
                        
//                         {Object.entries(dashboardData?.signal_statistics || {}).map(([type, count]) => (
//                           <Box key={type} sx={{ mb: 2 }}>
//                             <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
//                               <Typography variant="body2" sx={{ color: '#94a3b8' }}>
//                                 {type}
//                               </Typography>
//                               <Typography variant="body2" sx={{ color: '#fff' }}>
//                                 {count}
//                               </Typography>
//                             </Box>
//                             <LinearProgress
//                               variant="determinate"
//                               value={(count / (dashboardData?.signal_statistics?.TOTAL || 1)) * 100}
//                               sx={{ bgcolor: '#2d3748' }}
//                             />
//                           </Box>
//                         ))}
//                       </CardContent>
//                     </Card>
//                   </Grid>
//                 </Grid>
//               </Paper>
//             )}
//           </Box>
//         </Fade>
//       </Container>

//       {/* Notification Snackbar */}
//       <Snackbar
//         open={!!notification}
//         autoHideDuration={6000}
//         onClose={() => setNotification(null)}
//         anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
//       >
//         {notification && (
//           <Alert 
//             onClose={() => setNotification(null)} 
//             severity={notification.severity}
//             sx={{ width: '100%' }}
//           >
//             {notification.message}
//           </Alert>
//         )}
//       </Snackbar>

//       {/* Loading Backdrop */}
//       <Backdrop
//         sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
//         open={isLoading}
//       >
//         <CircularProgress color="inherit" />
//       </Backdrop>

//       {/* Custom CSS for animations */}
//       <style jsx global>{`
//         @keyframes pulse {
//           0% { opacity: 1; }
//           50% { opacity: 0.7; }
//           100% { opacity: 1; }
//         }
//       `}</style>
//     </Box>
//   );
// };

// export default SignalMonitorPage;
